import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '../api';
import {
  Globe, Network, Shield, Mail, Inbox, RotateCw, Send, ShieldAlert,
  Server, Activity, Users, HardDrive, Key, RefreshCw, ChevronRight,
  Plus, CheckCircle2, AlertTriangle, Layers, Map, Bell, BellOff
} from 'lucide-react';

// ─── Helper: ativa Web Push no browser/PWA ──────────────────────
function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - base64String.length % 4) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = window.atob(base64);
  return Uint8Array.from([...raw].map(c => c.charCodeAt(0)));
}

function PushButton() {
  const [status, setStatus] = React.useState('unknown');
  const [busy, setBusy] = React.useState(false);

  const refresh = async () => {
    if (!('Notification' in window) || !('serviceWorker' in navigator)) { setStatus('unsupported'); return; }
    if (Notification.permission === 'denied')  { setStatus('denied');  return; }
    const reg = await navigator.serviceWorker.ready;
    const sub = await reg.pushManager.getSubscription();
    setStatus(sub ? 'subscribed' : (Notification.permission === 'granted' ? 'granted' : 'default'));
  };
  React.useEffect(() => { refresh(); }, []);

  const enable = async () => {
    setBusy(true);
    try {
      const perm = await Notification.requestPermission();
      if (perm !== 'granted') { setStatus('denied'); return; }
      const { data } = await api.get('/push/vapid-public');
      if (!data.key) { alert('VAPID não configurado no servidor'); return; }
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(data.key),
      });
      const json = sub.toJSON();
      await api.post('/push/subscribe', {
        endpoint: json.endpoint,
        keys: json.keys,
        ua: navigator.userAgent,
      });
      setStatus('subscribed');
    } catch (e) { console.error(e); alert('Erro ao ativar notificações'); }
    finally { setBusy(false); }
  };

  const test = async () => {
    setBusy(true);
    try {
      const r = await api.post('/push/test');
      alert(`Enviado para ${r.data.sent} dispositivo(s).`);
    } catch (e) { alert(e.response?.data?.error || 'Erro'); }
    finally { setBusy(false); }
  };

  const disable = async () => {
    setBusy(true);
    try {
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.getSubscription();
      if (sub) {
        await api.delete('/push/subscribe', { data: { endpoint: sub.endpoint } });
        await sub.unsubscribe();
      }
      setStatus('granted');
    } catch (e) { console.error(e); }
    finally { setBusy(false); }
  };

  if (status === 'unsupported') return null;
  if (status === 'subscribed') {
    return (
      <div className="hrow" style={{gap:6}}>
        <button className="btn btn-secondary btn-sm" onClick={test} disabled={busy}>
          <Bell size={12}/> Testar alerta
        </button>
        <button className="btn btn-ghost btn-sm" onClick={disable} disabled={busy} title="Desativar">
          <BellOff size={12}/>
        </button>
      </div>
    );
  }
  if (status === 'denied') {
    return <span className="badge warn" title="Permissão negada nas configurações do navegador">Notificações bloqueadas</span>;
  }
  return (
    <button className="btn btn-secondary btn-sm" onClick={enable} disabled={busy}>
      <Bell size={13}/> Ativar alertas
    </button>
  );
}

export default function Dashboard() {
  const [stats, setStats] = useState({ domains: [], services: [], me: null });

  const load = async () => {
    try {
      const [d, s, m] = await Promise.allSettled([
        api.get('/domains'),
        api.get('/services').catch(() => ({ data: [] })),
        api.get('/me'),
      ]);
      setStats({
        domains:  d.status === 'fulfilled' ? d.value.data : [],
        services: s.status === 'fulfilled' ? s.value.data : [],
        me:       m.status === 'fulfilled' ? m.value.data?.user : null,
      });
    } catch {}
  };
  useEffect(() => { load(); const i = setInterval(load, 30000); return () => clearInterval(i); }, []);

  const isAdmin = stats.me?.role === 'admin' || stats.me?.canAll;
  const running = stats.services.filter(x => x.state === 'running').length;
  const total   = stats.services.length;
  const dnssecCount = stats.domains.filter(d => d.has_dnssec).length;
  const emailCount  = stats.domains.filter(d => d.has_email).length;
  const allOk = total > 0 && running === total;

  const today = new Date().toLocaleDateString('pt-BR', { weekday: 'long', day: 'numeric', month: 'long' });
  const greet = (() => {
    const h = new Date().getHours();
    if (h < 12) return 'Bom dia';
    if (h < 18) return 'Boa tarde';
    return 'Boa noite';
  })();

  const kpis = [
    { label: 'Domínios',           value: stats.domains.length, icon: Globe },
    isAdmin && { label: 'Containers ativos', value: `${running}/${total || 0}`, kind: allOk ? 'ok' : 'warn', icon: Server },
    isAdmin && { label: 'DNSSEC ativos',     value: dnssecCount, icon: Shield },
    isAdmin && { label: 'Com e-mail',        value: emailCount, icon: Mail },
  ].filter(Boolean);

  const features = [
    { icon: Globe,   title: 'Zona DNS',     desc: 'Edição completa A · AAAA · CNAME · MX · TXT · SRV · CAA' },
    { icon: RotateCw,title: 'DNS Reverso',  desc: 'IPv4/IPv6 com sub-delegações RFC 2317 e diagnóstico inline' },
    { icon: Shield,  title: 'DNSSEC',       desc: 'Cadeia de confiança + DS record copiável para registrar' },
    { icon: Layers,  title: 'RPKI',         desc: 'Wizard delegado · ROAs propagadas em tempo real' },
    { icon: Map,     title: 'Geofeed',      desc: 'Auto-publica linhas RFC 8805 por ASN/família' },
    { icon: Mail,    title: 'Contas E-mail',desc: 'Postfix + Dovecot + quotas + redirecionamentos' },
    { icon: Inbox,   title: 'Webmail',      desc: 'Roundcube com SSL automático Let\'s Encrypt' },
    { icon: Network, title: 'Nginx Proxy',  desc: 'Vhosts internos com SSL opcional e logs' },
    { icon: Users,   title: 'Permissões',   desc: 'Granular por domínio · auditoria completa' },
  ];

  return (
    <div className="stack-lg">
      <header className="row-between">
        <div>
          <h1 className="h1">{greet}{stats.me?.username ? `, ${stats.me.username}` : ''}.</h1>
          <p className="dim" style={{ fontSize: 14, marginTop: 4 }}>
            Visão geral da operação · {today}
          </p>
        </div>
        <div className="hrow" style={{ gap: 8 }}>
          <PushButton/>
          <button className="btn btn-secondary" onClick={load}>
            <RefreshCw size={13}/> Sincronizar
          </button>
          {isAdmin && (
            <Link to="/domains" style={{ textDecoration: 'none' }}>
              <button className="btn btn-primary">
                <Plus size={14}/> Novo domínio
              </button>
            </Link>
          )}
        </div>
      </header>

      {/* Status banner */}
      <div className="card" style={{
        borderColor: allOk ? 'rgba(63,185,80,0.3)' : 'rgba(210,153,34,0.3)',
        background: allOk
          ? 'linear-gradient(135deg, rgba(63,185,80,0.06), transparent 60%)'
          : 'linear-gradient(135deg, rgba(210,153,34,0.06), transparent 60%)'
      }}>
        <div className="card-body hrow" style={{ gap: 16, padding: 16 }}>
          <div style={{ width: 44, height: 44, borderRadius: 12,
                        background: allOk ? 'var(--ok-dim)' : 'var(--warn-dim)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            {allOk
              ? <CheckCircle2 size={22} color="var(--ok)"/>
              : <AlertTriangle size={22} color="var(--warn)"/>}
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 14, fontWeight: 600 }}>
              {allOk ? 'Tudo verde — operação saudável' : 'Atenção em algum serviço'}
            </div>
            <div className="dim" style={{ fontSize: 12.5, marginTop: 2 }}>
              {total > 0
                ? `${running} de ${total} containers em execução · ${stats.domains.length} domínios ativos`
                : `${stats.domains.length} domínios ativos`}
            </div>
          </div>
          {isAdmin && (
            <Link to="/services" style={{ textDecoration: 'none' }}>
              <button className="btn btn-secondary btn-sm">Ver serviços <ChevronRight size={12}/></button>
            </Link>
          )}
        </div>
      </div>

      {/* KPIs */}
      <div className="grid-4">
        {kpis.map((k, i) => (
          <div key={i} className="stat-card">
            <div className="row-between">
              <span className="stat-label">{k.label}</span>
              <k.icon size={14} color="var(--text-mute)"/>
            </div>
            <div className="stat-value" style={k.kind === 'warn' ? {color:'var(--warn)'} : k.kind === 'ok' ? {color:'var(--ok)'} : {}}>
              {k.value}
            </div>
          </div>
        ))}
      </div>

      {/* Domínios */}
      <div className="card">
        <div className="card-header">
          <Globe size={16} color="var(--accent)"/>
          <h3 className="h3">Seus domínios</h3>
          <span className="badge muted">{stats.domains.length} ativos</span>
          <div className="card-actions">
            <Link to="/domains" style={{ textDecoration: 'none' }}>
              <button className="btn btn-ghost btn-sm">Ver todos <ChevronRight size={12}/></button>
            </Link>
          </div>
        </div>
        {stats.domains.length === 0 ? (
          <div className="empty-state">
            <div className="empty-icon"><Globe size={26}/></div>
            <div className="empty-title">Sem domínios ainda</div>
            <div className="empty-desc">
              {isAdmin
                ? <Link to="/domains" style={{color:'var(--accent)'}}>Adicionar domínio →</Link>
                : 'Aguardando atribuição de domínios pelo administrador.'}
            </div>
          </div>
        ) : (
          <table className="table">
            <thead>
              <tr>
                <th style={{width:30}}></th>
                <th>Domínio</th>
                <th>IP</th>
                <th>Status</th>
                <th>Recursos</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {stats.domains.slice(0, 8).map(d => {
                const status = d.has_dns ? 'ok' : 'warn';
                return (
                  <tr key={d.id}>
                    <td><span className={`dot ${status}`}/></td>
                    <td>
                      <Link to={`/domains/${d.name}`} style={{color:'var(--text)', textDecoration:'none'}}>
                        <div style={{ fontWeight: 600 }}>{d.name}</div>
                      </Link>
                    </td>
                    <td className="mono dim" style={{ fontSize: 12 }}>{d.ipv4 || '—'}</td>
                    <td>
                      <span className={`badge ${status}`}>
                        {status === 'ok' ? 'Saudável' : 'Atenção'}
                      </span>
                    </td>
                    <td>
                      <div className="hrow" style={{ gap: 6 }}>
                        {d.has_dnssec && <span className="badge accent"><Shield size={10}/>DNSSEC</span>}
                        {d.has_email  && <span className="badge violet"><Mail size={10}/>E-mail</span>}
                      </div>
                    </td>
                    <td style={{ textAlign: 'right' }}>
                      <Link to={`/domains/${d.name}`} style={{textDecoration:'none'}}>
                        <button className="btn btn-secondary btn-sm">
                          Gerenciar <ChevronRight size={12}/>
                        </button>
                      </Link>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* O que você pode gerenciar */}
      <div>
        <div className="row-between" style={{ marginBottom: 12 }}>
          <div>
            <h2 className="h2">Dentro de cada domínio</h2>
            <p className="dim" style={{ fontSize: 13, marginTop: 4 }}>
              Tudo que o painel oferece para operar um domínio completo
            </p>
          </div>
        </div>
        <div className="grid-3">
          {features.map((c, i) => (
            <div key={i} className="card" style={{ padding: 16 }}>
              <div className="hrow" style={{ gap: 12, alignItems: 'flex-start' }}>
                <div style={{ width: 36, height: 36, borderRadius: 10,
                              background: 'linear-gradient(135deg, var(--accent-dim), var(--accent-2-dim))',
                              border: '1px solid var(--border)',
                              display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <c.icon size={18} color="var(--accent)"/>
                </div>
                <div>
                  <div style={{ fontSize: 13.5, fontWeight: 600 }}>{c.title}</div>
                  <div className="dim" style={{ fontSize: 12, marginTop: 4, lineHeight: 1.5 }}>{c.desc}</div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
